<app-accordion
	expandedMaxHeightClass="max-h-80"
	[label]="sectionLabel"
	[expanded]="isExpanded()"
	(expandedChange)="onExpandedChange($event)">
	<div class="flex flex-col gap-3 p-3">
		<!--
			One body height for every state, so the card does not resize as it loads — a floor rather
			than a fixed height, because the error state carries a retry button and needs the room.
		-->
		<div class="flex min-h-38 flex-col">
			@if (isLoading()) {
				<!-- Held until the first response lands, so the no-data message never stands in for it. -->
				<div class="flex flex-1 items-center justify-center" data-cy="trend-chart-loading">
					<ui-loading [showMessage]="false" />
				</div>
			} @else if (hasFailed()) {
				<ui-empty-state
					class="flex-1 items-center"
					data-cy="trend-chart-error"
					variant="error"
					iconName="alert-box"
					title="دریافت نمودار انجام نشد"
					primaryButtonLabel="تلاش مجدد"
					[showDescription]="false"
					[showButton]="true"
					(primaryClicked)="retry()" />
			} @else if (hasData()) {
				<!-- Latin reading order: prices in the left gutter, time ascending to the right. -->
				<div class="flex flex-1 flex-col" dir="ltr" role="img" [attr.aria-label]="chartLabel()">
					<div class="relative my-2 flex-1">
						@for (tick of view().yTicks; track tick.value) {
							<div
								class="absolute inset-x-0 flex -translate-y-1/2 items-center gap-3"
								[style.top.%]="tick.position">
								<span class="w-12 shrink-0 text-end text-2.75 whitespace-nowrap text-gray-600">{{
									tick.label
								}}</span>
								<span class="gridline h-px flex-1"></span>
							</div>
						}

						@if (view().lastPriceLine; as lastPrice) {
							<div
								class="pointer-events-none absolute inset-x-0 z-2 flex -translate-y-1/2 items-center gap-3"
								data-cy="trend-chart-last-price"
								[style.top.%]="lastPrice.position">
								<span class="w-12 shrink-0 text-end text-2.75 whitespace-nowrap">
									<span class="-me-1 rounded-sm bg-slate-600 px-1 text-gray-000">{{
										lastPrice.label
									}}</span>
								</span>
								<!-- Same right inset as the chart surface, so the line stops on the last point. -->
								<span class="mr-2 flex-1">
									<span class="last-price-line block h-px" [style.width.%]="lastPrice.end"></span>
								</span>
							</div>
						}

						<!--
							The box is sized by the wrapper, not the svg: an absolutely positioned svg takes its
							missing dimension from the viewBox aspect ratio rather than from the insets, which
							turned a 853x120 strip into an 853x853 square and pushed the whole series below the
							visible area.
						-->
						<div
							#chartSurface
							class="absolute inset-y-0 right-2 left-15 touch-pan-y"
							(pointercancel)="onChartPointerEnd()"
							(pointerdown)="onChartPointerDown($event)"
							(pointermove)="onChartPointerMove($event)"
							(pointerup)="onChartPointerEnd()">
							<svg
								aria-hidden="true"
								class="trend-draw h-full w-full overflow-visible"
								preserveAspectRatio="none"
								[attr.viewBox]="'0 0 ' + VIEW_SIZE + ' ' + VIEW_SIZE">
								@if (view().areaPath) {
									<defs>
										<linearGradient x1="0" y1="0" x2="0" y2="1" [attr.id]="areaGradientId">
											<stop offset="0" [attr.stop-color]="AREA_GRADIENT_FROM" />
											<stop offset="1" [attr.stop-color]="AREA_GRADIENT_TO" />
										</linearGradient>
									</defs>
									<path
										fill-opacity="0.3"
										[attr.d]="view().areaPath"
										[attr.fill]="'url(#' + areaGradientId + ')'" />
								}
								<path
									fill="none"
									stroke-width="3"
									stroke-linecap="round"
									stroke-linejoin="round"
									vector-effect="non-scaling-stroke"
									[attr.d]="view().linePath"
									[attr.stroke]="LINE_STROKE" />
							</svg>
							@if (activePoint(); as point) {
								<span
									class="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-stroke-100"
									[style.left.%]="point.x"></span>
								<span
									class="pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-slate-600 bg-gray-000"
									[style.left.%]="point.x"
									[style.top.%]="point.y"></span>
								<!--
									A zero-width wrapper pinned to the marker, so `justify-content` alone decides
									whether the bubble is centred on it or turned inward at the ends of the box. The
									padding is the gap to the marker, and sits inside the translate so it holds on
									both sides of the flip.
								-->
								<div
									class="pointer-events-none absolute z-3 flex w-0"
									[class.-translate-y-full]="!isTooltipBelow()"
									[style.justify-content]="tooltipAlign()"
									[style.left.%]="point.x"
									[style.padding-bottom.px]="isTooltipBelow() ? 0 : 6"
									[style.padding-top.px]="isTooltipBelow() ? 6 : 0"
									[style.top.%]="point.y">
									<span
										class="whitespace-nowrap rounded-lg border border-stroke-100 bg-gray-000 px-1.5 py-0.5 text-center text-2.75 text-gray-900 shadow-sm">
										<span class="block font-medium">{{ point.priceLabel }}</span>
										<span class="block text-gray-600">{{ point.timeLabel }}</span>
									</span>
								</div>
							}
						</div>
					</div>

					<!--
						Round-time ticks land wherever the clock puts them rather than on even fractions of
						the box, so each label is placed by percentage instead of flex-spaced. The zero-width
						wrapper aligns the label around its own tick without a transform: centred for the
						round readings, and turned inward for the two bounds so neither hangs outside the box.

						This row is also what gets measured for the label budget, so it has to span exactly
						the chart box — the same insets as the svg wrapper above.
					-->
					<div class="relative mr-2 ml-15 h-4 text-2.75 text-gray-600">
						@for (tick of view().xTicks; track tick.value) {
							<span
								class="absolute flex w-0"
								[style.justify-content]="tick.align"
								[style.left.%]="tick.position">
								<span class="whitespace-nowrap">{{ tick.label }}</span>
							</span>
						}
					</div>
				</div>
			} @else {
				<!-- Reached by a session that reported no trades of its own — not by one still loading. -->
				<ui-empty-state
					class="flex-1 items-center"
					data-cy="trend-chart-empty"
					variant="empty"
					title="داده ای جهت نمایش یافت نشد"
					[showDescription]="false" />
			}
		</div>

		<!-- Today's line is the short view; ایزی چارت is the symbol's full history, whatever state the card is in. -->
		<ui-button
			variant="mute"
			size="md"
			label="ایزی چارت"
			extraButtonClasses="w-full gap-1 text-3.25"
			data-cy="trend-chart-easy-chart"
			(clicked)="openEasyChart()">
			<ui-icon leftIcon width="20" height="20" iconName="easychart" aria-hidden="true" />
		</ui-button>
	</div>
</app-accordion>
